/* eslint-disable react/jsx-no-useless-fragment */
import type React from 'react';
import { useMemo } from 'react';
import useSWR, { type KeyedMutator } from 'swr';
import generateRandomId from '../../helpers/generateRandomId';
import useSwrCache from '../../hooks/data/useSwrCache';
import Loading from '../Loading/Loading';

interface Props<T> {
    cacheKey?: string;
    service: () => Promise<T>;

    /**
     * Sometimes we want loading to only show when its loading,
     * not just when its re-validating. Sometimes revalidation
     * we want to show loading as well. Revalidation = mutation
     * or refreshing the call
     */
    showLoadingOnValidating?: boolean;
    // swrOptions?: Parameters<typeof useSWR>[2];

    loadingFallback: React.JSX.Element | React.JSX.Element[];

    children: (props: {
        data: T | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: any;
        isLoading: boolean;

        /** running this on empty will refresh the query */
        mutate: KeyedMutator<T>;
        refresh: () => void;

        /** These will expose helpers to invalidate cache */
        swrCacheHelpers: ReturnType<typeof useSwrCache>;
    }) => React.JSX.Element | React.JSX.Element[] | null | undefined;
}

const QueryComponent = <T,>({
    cacheKey,
    service,
    loadingFallback,
    showLoadingOnValidating = false,
    // swrOptions,
    children,
}: Props<T>) => {
    const swrCacheHelpers = useSwrCache();
    const memoizedService = useMemo(() => service, [service]);
    const swrCacheKey = useMemo(() => cacheKey || generateRandomId(), [cacheKey]);

    const { data, error, isLoading, mutate, isValidating } = useSWR(swrCacheKey, () => memoizedService(), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const refresh = () => mutate();

    const loading = showLoadingOnValidating ? isValidating || isLoading : isLoading;

    return (
        <Loading isLoading={loading} loadingFallback={loadingFallback}>
            {() =>
                children({
                    data,
                    error,
                    isLoading,
                    refresh,
                    mutate,
                    swrCacheHelpers,
                })
            }
        </Loading>
    );
};

export default QueryComponent;
